import React, { useState, useEffect, useContext, createContext, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'src/rematch';

interface ViewOnlyModeContextType {
  isViewOnlyMode: boolean;
  isLoading: boolean;
  checkViewOnlyStatus: () => Promise<void>;
  userEmail: string | null;
}

const ViewOnlyModeContext = createContext<ViewOnlyModeContextType | null>(null);

interface ViewOnlyModeProviderProps {
  children: ReactNode;
  _userType?: string;
  email?: string;
  isAuthenticated?: boolean;
}

const ViewOnlyModeProvider: React.FC<ViewOnlyModeProviderProps> = ({ 
  children, 
  _userType, 
  email,
  isAuthenticated 
}) => {
  const [isViewOnlyMode, setIsViewOnlyMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkViewOnlyStatus = async () => {
    if (!isAuthenticated || !email) {
      setIsViewOnlyMode(false);
      return;
    }

    setIsLoading(true);
    
    try {
      // Check if user is in view-only mode by looking at session storage
      // or checking if they came from migration choice with view-only selection
      const viewOnlyMode = sessionStorage.getItem('view_only_mode');
      const isViewOnly = viewOnlyMode === 'true';
      
      setIsViewOnlyMode(isViewOnly);
    } catch (error) {
      setIsViewOnlyMode(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      checkViewOnlyStatus();
    }
  }, [isAuthenticated, email]);

  const contextValue: ViewOnlyModeContextType = {
    isViewOnlyMode,
    isLoading,
    checkViewOnlyStatus,
    userEmail: email || null,
  };

  return (
    <ViewOnlyModeContext.Provider value={contextValue}>
      {children}
    </ViewOnlyModeContext.Provider>
  );
};

const mapState = (state: RootState) => ({
  _userType: state.auth.userType,
  email: state.auth.email,
  isAuthenticated: state.auth.isAuthenticate,
});

const ConnectedViewOnlyModeProvider = connect(mapState)(ViewOnlyModeProvider);

const useViewOnlyMode = (): ViewOnlyModeContextType => {
  const context = useContext(ViewOnlyModeContext);
  if (!context) {
    throw new Error('useViewOnlyMode must be used within a ViewOnlyModeProvider');
  }
  return context;
};

// Higher-order component to wrap forms and prevent submission in view-only mode
export const withViewOnlyProtection = <P extends object>(
  WrappedComponent: React.ComponentType<P>
) => (props: P) => {
  const { isViewOnlyMode } = useViewOnlyMode();

  const protectedProps = {
    ...props,
    disabled: isViewOnlyMode,
    readOnly: isViewOnlyMode,
  } as P;

  return <WrappedComponent {...protectedProps} />;
};

export { ConnectedViewOnlyModeProvider as ViewOnlyModeProvider, useViewOnlyMode };